import { useEffect, useRef, useState } from 'react';
import {
  registerToastHandler,
  type ToastShowOptions,
  type ToastType,
} from '@wlb/shared/platform/notifier';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import './toast.css';

interface ActiveToast {
  id: number;
  type: ToastType;
  message: string;
}

const DEFAULT_DURATION = 4000;

/**
 * Host for `ToastService` in the admin panel. Shared code only calls
 * `ToastService.show`; this component is what renders it.
 */
export function Toast() {
  const { t } = useTranslation('admin');
  const [toast, setToast] = useState<ActiveToast | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextIdRef = useRef(0);

  useEffect(() => {
    registerToastHandler((options: ToastShowOptions) => {
      const message = options.message ?? options.subtitle ?? options.title ?? '';
      if (!message) {
        return;
      }
      nextIdRef.current += 1;
      setToast({
        id: nextIdRef.current,
        type: options.type ?? 'info',
        message,
      });
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(
        () => setToast(null),
        options.duration ?? DEFAULT_DURATION,
      );
    });

    return () => {
      registerToastHandler(null);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const isError = toast?.type === 'error';

  const body = toast ? (
    <div key={toast.id} className={`admin-toast admin-toast--${toast.type}`}>
      {/* Severity is otherwise carried by colour alone. */}
      <span className="sr-only">{t(`common.toastType.${toast.type}`)}: </span>
      {toast.message}
    </div>
  ) : null;

  // Two static regions rather than one with a swapped `aria-live`: assistive
  // tech only reliably honours the politeness set when the region is created,
  // and a failure should interrupt rather than queue behind other output.
  return (
    <>
      <div className="admin-toast-region" aria-live="polite" aria-atomic="true">
        {isError ? null : body}
      </div>
      <div className="admin-toast-region" role="alert" aria-atomic="true">
        {isError ? body : null}
      </div>
    </>
  );
}
